import React, { useState, useEffect, useCallback, useMemo } from "react";
import { serverFetch } from "../utils/serverApi";
import "./Mail.css";

const DEFAULT_CACHE_KEY = "myquest_welcome_default_v1";
const SAMPLE_NAME = "Ada";
const SAMPLE_EMAIL = "ada@example.com";

const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s);
const fill = (s) =>
  s.replace(/\{\{\s*name\s*\}\}/g, SAMPLE_NAME).replace(/\{\{\s*email\s*\}\}/g, SAMPLE_EMAIL);

const WelcomeEmailPage = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saved, setSaved] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [placeholders, setPlaceholders] = useState(["{{name}}", "{{email}}"]);
  const [defaultTpl, setDefaultTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const d = await serverFetch("/api/admin/mail/templates/welcome");
      setSaved(d);
      setSubject(d.subject || "");
      setBody(d.body || "");
      if (Array.isArray(d.placeholders)) setPlaceholders(d.placeholders);
      if (d.defaults && d.defaults.subject && d.defaults.body) {
        setDefaultTpl({ subject: d.defaults.subject, body: d.defaults.body });
      } else if (d.isDefault) {
        const def = { subject: d.subject, body: d.body };
        setDefaultTpl(def);
        try {
          localStorage.setItem(DEFAULT_CACHE_KEY, JSON.stringify(def));
        } catch (e) {
          /* storage unavailable */
        }
      } else {
        try {
          const cached = JSON.parse(localStorage.getItem(DEFAULT_CACHE_KEY));
          setDefaultTpl(cached && cached.subject && cached.body ? cached : null);
        } catch (e) {
          setDefaultTpl(null);
        }
      }
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = !!saved && (subject !== saved.subject || body !== saved.body);
  const invalid = !subject.trim() || !body.trim() || subject.length > 200 || body.length > 20000;

  const put = async (tpl, okText) => {
    setSaving(true);
    setBanner(null);
    try {
      await serverFetch("/api/admin/mail/templates/welcome", {
        method: "PUT",
        body: { subject: tpl.subject, body: tpl.body },
      });
      setSaved((s) => ({ ...(s || {}), subject: tpl.subject, body: tpl.body, isDefault: false }));
      setSubject(tpl.subject);
      setBody(tpl.body);
      setBanner({ type: "ok", text: okText });
    } catch (e) {
      setBanner({ type: "err", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const preview = useMemo(() => fill(body), [body]);

  if (loading) return <div className="mail-page">Loading welcome email...</div>;
  if (loadError)
    return (
      <div className="mail-page">
        <h1>Welcome Email</h1>
        <div className="mail-banner err" role="alert">{loadError}</div>
        <button type="button" className="mail-btn primary" onClick={load}>Retry</button>
      </div>
    );

  const isDefault = !!(saved && saved.isDefault);

  return (
    <div className="mail-page">
      <h1>Welcome Email</h1>
      <p className="mail-hint">
        Sent to new app users when they sign up. {isDefault ? "Currently using the default template." : "Currently using a custom template."}
      </p>

      <label htmlFor="we-subject">Subject ({subject.length}/200)</label>
      <input id="we-subject" type="text" maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} />

      <label htmlFor="we-body">Body ({body.length}/20000, plain text or HTML)</label>
      <div style={{ marginBottom: ".4rem" }}>
        {placeholders.map((ph) => (
          <button type="button" key={ph} className="mail-chip" title="Insert at end of body" onClick={() => setBody((b) => b + ph)}>
            {ph}
          </button>
        ))}
      </div>
      <textarea id="we-body" maxLength={20000} value={body} onChange={(e) => setBody(e.target.value)} />

      <label>Preview (sample name: {SAMPLE_NAME})</label>
      <div className="mail-preview">
        <div style={{ fontWeight: 700, marginBottom: ".5rem" }}>{fill(subject)}</div>
        {looksLikeHtml(body) ? (
          // Sandboxed iframe so admin-authored HTML can never run in the admin origin.
          <iframe title="Welcome email preview" sandbox="" srcDoc={preview} style={{ width: "100%", height: 320, border: 0, background: "#fff" }} />
        ) : (
          <pre>{preview}</pre>
        )}
      </div>

      {banner && <div className={`mail-banner ${banner.type}`} role="status">{banner.text}</div>}

      <div className="mail-actions">
        <button type="button" className="mail-btn primary" disabled={saving || !dirty || invalid} onClick={() => put({ subject, body }, "Welcome email saved.")}>
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          className="mail-btn"
          disabled={saving || !defaultTpl || isDefault}
          onClick={() => {
            if (window.confirm("Replace the current template with the default text?")) put(defaultTpl, "Reset to default text.");
          }}
        >
          Reset to default
        </button>
        <button type="button" className="mail-btn" disabled={saving || !dirty} onClick={() => { setSubject(saved.subject); setBody(saved.body); }}>
          Discard changes
        </button>
      </div>
      {!defaultTpl && !isDefault && (
        <p className="mail-hint">
          Reset is unavailable: the server only returns the default text while the template is unmodified, and this browser has not seen it yet.
        </p>
      )}
    </div>
  );
};

export default WelcomeEmailPage;
